import { useState, useEffect } from 'react';
import { Field, RenderField } from '@oms/ui-field';
import { NumberInput } from 'components/NumberInput'; //'@oms/ui-number-input';
import { TradeUtil, formatDate } from 'utils/trade';
import { warningValidation } from 'utils/validation/tradeWarningValidation';
import { TextInput } from '@oms/ui-text-input';
import { useForm, useFormState } from 'react-final-form';
import { useItemSector } from 'context/ItemSectorContext';
import { Checkbox } from '@oms/ui-checkbox';
import * as DateFNS from 'date-fns';
import { InfoBubble } from '@oms/ui-info-bubble';
import { DateInput } from '@oms/ui-date-input';
import { Table, Td, Th } from '@oms/ui-table';
import { Stack } from '@oms/ui-stack';
import moment from 'moment';
import { useGetCloseDates } from 'components/Trade/Queries/useGetCloseDates';
import { Text } from '@oms/ui-text';
import { useLast } from '../Queries/useLast';
import { normalizeNumber } from 'utils/form';
import { marketCloseTime, marketEndOfDay } from 'constants/marketTime';

export const getItemAndSector = (itemSectorValue: any) => {
  const [item = '', useSector = ''] = itemSectorValue?.split('.');
  return {
    item,
    useSector,
  };
};

type Props = {
  editMode: boolean;
  hidden: boolean;
  sell: React.ReactChild;
  holdings?: number;
};

export const SellForm = ({ editMode, sell, hidden, holdings = 0 }: Props) => {
  const form = useForm();
  const { values } = useFormState();

  const { data: closeDates } = useGetCloseDates();
  const itemSectorValue = useItemSector();
  const { data: last } = useLast(itemSectorValue);
  let lastDay = new Date();
  lastDay.setDate(lastDay.getDate() + 14);
  const closeTime = marketCloseTime;
  const endOfDay = marketEndOfDay;
  let currentTime = moment();
  const currentDate = moment().format('LL');
  const closeTimeTo = moment(`${currentDate} ${closeTime}`);
  const nextDay = moment()
    .add(1, 'days')
    .format('YYYY-MM-DD')
    .toString();
  const endOfDayTo = moment(`${nextDay} ${endOfDay}`, 'YYYY-MM-DD HH:mm');
  const isMarketClose = currentTime >= closeTimeTo && currentTime <= endOfDayTo;
  const [start, setStart] = useState(
    isMarketClose
      ? new Date(nextDay)
      : values.expirationDate || TradeUtil.getDefault('start'),
  );
  const minDate = isMarketClose ? new Date(nextDay) : new Date();
  useEffect(() => {
    form.change('expirationDate', `${moment(start).format('YYYY-MM-DD')}`);
  }, [start, form]);
  const expirationDate = (date: Date) => {
    setStart(formatDate(date));
  };

  const holdingsPluralText = holdings > 1 ? 'aksjer' : 'aksje';
  const zeroStocksInHoldings = holdings === 0 || holdings === undefined;
  const isWeekday = (date: Date) => {
    const day = DateFNS.getDay(date);
    return day !== 0 && day !== 6;
  };
  return (
    <div hidden={hidden}>
      <Stack gap={5} /*p={2}*/ width="100%">
        <Field as={TextInput} name="ticker" label="Ticker / Selskap" disabled />
        <Field
          as={NumberInput}
          lang="nb"
          inputMode="numeric"
          name="volume"
          label="Totalt Antall/Volum"
          message={
            holdings
              ? `Du har ${holdings} ${holdingsPluralText} i beholdningen`
              : undefined
          }
          min="0"
          disabled={zeroStocksInHoldings}
          flex={1}
        />

        <Stack gap={4} orientation="horizontal" width="100%">
          <Field
            name="useOpenVolume"
            as={Checkbox}
            label="Jeg vil skjule deler av min ordre"
            onChange={() => form?.resetFieldState('openVolume')}
            disabled={editMode || zeroStocksInHoldings}
          />

          <InfoBubble aria-label="Info" variant="default">
            {editMode
              ? 'For å endre på dette må du trekke ordren og legge inn en ny.'
              : `Du kan velge å skjule deler av din ordre ved å her taste inn det
              volumet som skal være synlig. Ordrestørrelse må være større enn 100 000 NOK`}
          </InfoBubble>
        </Stack>
        {values?.useOpenVolume && (
          <Field
            as={NumberInput}
            lang="nb"
            inputMode="numeric"
            disabled={zeroStocksInHoldings}
            name="openVolume"
            label="Synlig volum"
          />
        )}
        <Field
          as={NumberInput}
          lang="nb"
          inputMode="decimal"
          name="limit"
          min="0"
          disabled={zeroStocksInHoldings}
          label="Pris/Limit"
          flex={1}
          message={
            values.limit && (
              <Text color="warning">
                {warningValidation(values.limit, last)}
              </Text>
            )
          }
        />

        <Stack gap={4} orientation="horizontal" width="100%">
          <Field
            name="useStopLoss"
            disabled={zeroStocksInHoldings}
            as={Checkbox}
            label="Stop loss"
            onChange={() => form?.resetFieldState('stopLossLimit')}
          />
          <InfoBubble aria-label="Info" variant="default">
            Stop loss-ordre blir aktivert (sendt til børs) når sist omsatt
            treffer eller bryter Trigger pris. Trigger pris bør derfor være
            høyere enn Pris/Limit.
          </InfoBubble>
        </Stack>
        {values?.useStopLoss && (
          <Field
            as={NumberInput}
            lang="nb"
            disabled={zeroStocksInHoldings}
            inputMode="decimal"
            name="stopLossLimit"
            label="Trigger pris"
          />
        )}

        <RenderField
          as={DateInput}
          name="expirationDate"
          label="Gyldig til"
          onChange={expirationDate}
          value={new Date(start)}
          excludeDates={closeDates?.data}
          minDate={minDate}
          maxDate={lastDay}
          filterDate={isWeekday}
        />

        <Table density="dense" variant="keyValue">
          <tbody>
            <tr />
            <tr>
              <Th>Ordre</Th>
              <Td type="number" suffix="kr">
                {values?.limit && values?.volume
                  ? normalizeNumber(values?.limit) *
                    normalizeNumber(values?.volume)
                  : 0}
              </Td>
            </tr>
            <tr>
              <Th>Total</Th>
              <Td type="number" suffix="kr">
                {values?.limit && values?.volume
                  ? normalizeNumber(values?.limit) *
                    normalizeNumber(values?.volume)
                  : 0}
              </Td>
            </tr>
          </tbody>
        </Table>
        {sell}
      </Stack>
    </div>
  );
};
